/**
 * @fileoverview Sudoku Result Overlay Component
 * @description Shows game result when completed or failed
 */

import React, { memo, useEffect, useRef } from "react";
import {
  View,
  Text,
  Pressable,
  Animated,
  useColorScheme,
  Modal,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import LottieView from "lottie-react-native";
import { SudokuResult, DIFFICULTY_CONFIGS } from "../types";
import { formatTimeVerbose } from "../utils/sudokuValidator";
import { getLottieSource } from "@/lib/lottie-registry";

/** Default lottie keys */
const DEFAULT_WIN_LOTTIE = "Partying_Emoji";
const DEFAULT_LOSE_LOTTIE = "Loudly_Crying_Emoji";
const DEFAULT_PERFECT_LOTTIE = "Success";

interface SudokuResultOverlayProps {
  /** Game result */
  result: SudokuResult;
  /** Whether to show the overlay */
  visible: boolean;
  /** Called when "Play Again" is pressed */
  onPlayAgain: () => void;
  /** Called when "Back to Menu" is pressed */
  onBackToMenu: () => void;
  /** Called when "Share" is pressed */
  onShare?: () => void;
  /** Custom lottie key for win animation */
  winLottieKey?: string;
  /** Custom lottie key for lose animation */
  loseLottieKey?: string;
  /** Custom lottie key for perfect score animation */
  perfectLottieKey?: string;
}

/**
 * Game result overlay with animations
 */
export const SudokuResultOverlay = memo<SudokuResultOverlayProps>(
  ({
    result,
    visible,
    onPlayAgain,
    onBackToMenu,
    onShare,
    winLottieKey,
    loseLottieKey,
    perfectLottieKey,
  }) => {
    const colorScheme = useColorScheme();
    const isDark = colorScheme === "dark";

    const fadeAnim = useRef(new Animated.Value(0)).current;
    const scaleAnim = useRef(new Animated.Value(0.8)).current;
    const lottieRef = useRef<LottieView>(null);

    const config = DIFFICULTY_CONFIGS[result.difficulty];
    const isWin = result.completed && !result.failed;

    // Determine which lottie to use
    const getLottieAnimation = () => {
      if (!isWin) {
        // Lost game
        const loseKey = loseLottieKey || DEFAULT_LOSE_LOTTIE;
        return getLottieSource(loseKey) || getLottieSource(DEFAULT_LOSE_LOTTIE);
      }

      if (result.is_perfect) {
        // Perfect score
        const perfectKey = perfectLottieKey || DEFAULT_PERFECT_LOTTIE;
        return getLottieSource(perfectKey) || getLottieSource(DEFAULT_PERFECT_LOTTIE);
      }

      // Regular win
      const winKey = winLottieKey || DEFAULT_WIN_LOTTIE;
      return getLottieSource(winKey) || getLottieSource(DEFAULT_WIN_LOTTIE);
    };

    const lottieSource = getLottieAnimation();

    useEffect(() => {
      if (visible) {
        Animated.parallel([
          Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 300,
            useNativeDriver: true,
          }),
          Animated.spring(scaleAnim, {
            toValue: 1,
            friction: 8,
            tension: 40,
            useNativeDriver: true,
          }),
        ]).start();

        // Play lottie animation
        if (lottieRef.current) {
          lottieRef.current.play();
        }
      } else {
        fadeAnim.setValue(0);
        scaleAnim.setValue(0.8);
      }
    }, [visible, fadeAnim, scaleAnim]);

    if (!visible) return null;

    return (
      <Modal
        visible={visible}
        transparent
        animationType="none"
        statusBarTranslucent
      >
        <View
          style={{
            flex: 1,
            backgroundColor: "rgba(0,0,0,0.7)",
            justifyContent: "center",
            alignItems: "center",
            padding: 24,
          }}
        >
          <Animated.View
            style={{
              opacity: fadeAnim,
              transform: [{ scale: scaleAnim }],
              width: "100%",
              maxWidth: 360,
              backgroundColor: isDark ? "#1F2937" : "#FFFFFF",
              borderRadius: 24,
              padding: 24,
              alignItems: "center",
            }}
          >
            {/* Lottie animation */}
            <View style={{ width: 120, height: 120, marginBottom: 16 }}>
              {lottieSource && (
                <LottieView
                  ref={lottieRef}
                  source={lottieSource}
                  style={{ width: "100%", height: "100%" }}
                  loop={false}
                  autoPlay={false}
                />
              )}
            </View>

            {/* Title */}
            <Text
              style={{
                fontSize: 28,
                fontWeight: "800",
                color: isWin ? "#10B981" : "#EF4444",
                marginBottom: 8,
              }}
            >
              {isWin ? "Gefeliciteerd! 🎉" : "Helaas..."}
            </Text>

            {/* Subtitle */}
            <Text
              style={{
                fontSize: 16,
                color: isDark ? "#9CA3AF" : "#6B7280",
                marginBottom: 20,
                textAlign: "center",
              }}
            >
              {isWin
                ? result.is_perfect
                  ? "Perfect! Geen fouten en geen hints!"
                  : "Je hebt de puzzel opgelost!"
                : "Te veel fouten gemaakt"}
            </Text>

            {/* Stats */}
            <View
              style={{
                width: "100%",
                backgroundColor: isDark ? "#111827" : "#F9FAFB",
                borderRadius: 16,
                padding: 16,
                marginBottom: 20,
              }}
            >
              {/* Difficulty */}
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: 12,
                }}
              >
                <Text style={{ fontSize: 14, color: isDark ? "#9CA3AF" : "#6B7280" }}>
                  Moeilijkheid
                </Text>
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <View
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: config.color,
                    }}
                  />
                  <Text
                    style={{
                      fontSize: 14,
                      fontWeight: "600",
                      color: isDark ? "#F9FAFB" : "#111827",
                    }}
                  >
                    {config.label}
                  </Text>
                </View>
              </View>

              {/* Time */}
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: 12,
                }}
              >
                <Text style={{ fontSize: 14, color: isDark ? "#9CA3AF" : "#6B7280" }}>
                  Tijd
                </Text>
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: "600",
                    color: isDark ? "#F9FAFB" : "#111827",
                  }}
                >
                  {formatTimeVerbose(result.time_seconds)}
                </Text>
              </View>

              {/* Mistakes */}
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: 12,
                }}
              >
                <Text style={{ fontSize: 14, color: isDark ? "#9CA3AF" : "#6B7280" }}>
                  Fouten
                </Text>
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: "600",
                    color:
                      result.mistakes > 0
                        ? "#EF4444"
                        : isDark
                        ? "#F9FAFB"
                        : "#111827",
                  }}
                >
                  {result.mistakes}
                </Text>
              </View>

              {/* Hints */}
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <Text style={{ fontSize: 14, color: isDark ? "#9CA3AF" : "#6B7280" }}>
                  Hints gebruikt
                </Text>
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: "600",
                    color: isDark ? "#F9FAFB" : "#111827",
                  }}
                >
                  {result.hints_used}
                </Text>
              </View>

              {/* Points (if earned) */}
              {result.points_earned > 0 && (
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    marginTop: 12,
                    paddingTop: 12,
                    borderTopWidth: 1,
                    borderTopColor: isDark ? "#374151" : "#E5E7EB",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 14,
                      color: isDark ? "#9CA3AF" : "#6B7280",
                    }}
                  >
                    Punten verdiend
                  </Text>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <Ionicons name="star" size={16} color="#F59E0B" />
                    <Text
                      style={{
                        fontSize: 16,
                        fontWeight: "700",
                        color: "#F59E0B",
                      }}
                    >
                      +{result.points_earned}
                    </Text>
                  </View>
                </View>
              )}
            </View>

            {/* Buttons */}
            <View style={{ width: "100%", gap: 12 }}>
              {/* Play Again */}
              <Pressable
                onPress={onPlayAgain}
                accessibilityRole="button"
                accessibilityLabel="Opnieuw spelen"
                style={({ pressed }) => ({
                  width: "100%",
                  paddingVertical: 14,
                  borderRadius: 12,
                  backgroundColor: pressed ? "#2563EB" : "#3B82F6",
                  alignItems: "center",
                })}
              >
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: "600",
                    color: "#FFFFFF",
                  }}
                >
                  Opnieuw spelen
                </Text>
              </Pressable>

              {/* Back to Menu */}
              <Pressable
                onPress={onBackToMenu}
                accessibilityRole="button"
                accessibilityLabel="Terug naar menu"
                style={({ pressed }) => ({
                  width: "100%",
                  paddingVertical: 14,
                  borderRadius: 12,
                  backgroundColor: pressed
                    ? isDark
                      ? "#374151"
                      : "#E5E7EB"
                    : isDark
                    ? "#374151"
                    : "#F3F4F6",
                  alignItems: "center",
                })}
              >
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: "600",
                    color: isDark ? "#F9FAFB" : "#374151",
                  }}
                >
                  Terug naar menu
                </Text>
              </Pressable>
            </View>
          </Animated.View>
        </View>
      </Modal>
    );
  }
);

SudokuResultOverlay.displayName = "SudokuResultOverlay";
